<script lang="ts" setup>
import { UserRole } from 'cesg-shared/dist/models';
import { computed } from 'vue';
import CESGRoles from './CESGRoles.vue';

const roles = Object.values(UserRole);

export interface CESGRolesSelectorProps {
  data: {
    role?: string;
    availableRoles?: UserRole[];
  };
  disabled?: boolean;
}

const props = defineProps<CESGRolesSelectorProps>();

const emit = defineEmits<{
  (event: 'update:role', value: UserRole): void;
}>();

const isUserRole = (value: string): value is UserRole => {
  return roles.includes(value as UserRole);
};

const availableRoles = computed(() => {
  if (props.data.availableRoles) {
    return roles.filter((role) => props.data.availableRoles?.includes(role));
  }
  return roles;
});

const rolesForSelect = computed<UserRole[]>(() => {
  return availableRoles.value;
});

const selectedRoleMissingFromChoices = computed(() => {
  const role = props.data.role;
  return !!(role && isUserRole(role) && !availableRoles.value.includes(role));
});

const selectedRole = computed<string>(() => {
  const role = props.data.role;
  if (role && isUserRole(role)) {
    return role;
  }
  return rolesForSelect.value[0] ?? '';
});

const onRoleChange = (event: Event) => {
  const nextValue = (event.target as HTMLSelectElement).value;
  if (isUserRole(nextValue) && rolesForSelect.value.includes(nextValue)) {
    emit('update:role', nextValue);
  }
};
</script>

<template>
  <div class="select is-fullwidth">
    <select
      :value="selectedRole"
      :disabled="props.disabled || rolesForSelect.length === 0"
      @change="onRoleChange"
    >
      <option
        v-if="selectedRoleMissingFromChoices"
        :value="selectedRole"
        hidden
      >
        <CESGRoles :data="{ role: selectedRole }" />
      </option>
      <option v-if="rolesForSelect.length === 0" value="" disabled>-</option>
      <option v-for="role in rolesForSelect" :key="role" :value="role">
        <CESGRoles :data="{ role }" />
      </option>
    </select>
  </div>
</template>

<style scoped lang="scss">
.select select:disabled {
  color: #4b5563;
  opacity: 1;
}
</style>
